import { PermissionDto } from './../../../models/dto/permission.dto';
import { PermissionType } from './../../../models/enum/permission.enum';
import { Constants } from './../../../../constants';
import { CreatePermissionDto } from './../../../models/dto/create-permissions.dto';
import { HelperService } from './../../../Services/helper.service';
import { CreateUpdateUserDto } from '../../../models/dto/create-update-user.dto';
import { HttpService } from './../../../Services/http.service';
import { OrganizationsDto } from './../../../models/dto/organization.dto';
import { CustomerDto } from './../../../models/dto/customer.dto';
import { UserDto } from '../../../models/dto/user.dto';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Component, EventEmitter, Input, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SearchCountryField, CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input-gg';
import { HttpErrorResponse } from '@angular/common/http';


@Component({
  selector: 'app-create-edit-user-modal',
  templateUrl: './create-edit-user-modal.component.html',
  styleUrls: ['./create-edit-user-modal.component.css']
})
export class CreateEditUserModalComponent {
  form: UntypedFormGroup;
  childForm: UntypedFormGroup;
  modalReference: NgbModalRef;
  customerList: CustomerDto[] = [];
  orgList: OrganizationsDto[] = [];
  permissionList: string[] = [];
  permissionItemsBefore: PermissionType[] = [];
  existUserErr = false;
  incorrectOrgErr = false;
  incorrectPermissionErr = false;
  showRequestLoader = false;
  otherError: string;
  showOtherError = false;
  title: string;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates];
  maxLength = 15;
  separateDialCode = true;

  @Input() userData: UserDto;
  @Output() successUpdateTable = new EventEmitter<boolean>(); // true for success create and false for success update
  @Output() showErrorModal = new EventEmitter<any>();
  @ViewChild('classic') public templateRef: TemplateRef<any>;

  ngbModalOptions: NgbModalOptions = {
    backdrop: 'static',
    keyboard: false,
    centered: true,
    size: 'md'
  };

  constructor(
    private fb: UntypedFormBuilder,
    public modalService: NgbModal,
    private http: HttpService,
    private helper: HelperService) {
    this.form = this.fb.group({
      firstName: [null, [Validators.required, Validators.minLength(2)]],
      lastName: [null, [Validators.required, Validators.minLength(2)]],
      email: [null, [Validators.required, Validators.pattern(Constants.emailRegex)]],
      phone: [null, [Validators.required]],
      customer: [null, Validators.required],
      organization: [null, Validators.required],
      permission: [null, Validators.required],
      notes: []
    });
    this.getAllCustomers();
    this.getAllPermissions();
  }

  getAllCustomers() {
    this.http.getCustomerList().subscribe((resp) => {
      this.customerList = resp;
    })
  }

  getAllPermissions() {
    this.http.getPermission().subscribe((resp) => {
      this.permissionList = resp;
    })
  }

  subscribeOnControlChanges() {
    this.customerControl.valueChanges.subscribe((value) => {
      if (value) {
        this.http.getOrganizationsByCustomerId(value).subscribe((resp) => {
          this.orgList = resp;
          const item = this.orgList.some(el => { return el.id === this.orgControl.value })
          if (!item) { this.orgControl.setValue(''); }
        });
      }
    });

    this.permissionControl.valueChanges.subscribe((value: PermissionType[]) => { // change admin/user permission if exist another admin/user permission
      if (!value) return;
      const isCustomerAdmin = value.includes(PermissionType.customerAdmin);
      const isPortalAdmin = value.includes(PermissionType.portalAdmin);
      const isCustomerUser = value.includes(PermissionType.customerUser);
      const isPortalUser = value.includes(PermissionType.portalUser);
      if (this.permissionItemsBefore.length > 0) {
        let admin;
        let user;
        if (isCustomerAdmin && isPortalAdmin) {
          admin = value.filter(el => {
            return (this.permissionItemsBefore.includes(PermissionType.customerAdmin) && el !== PermissionType.customerAdmin)
              || (this.permissionItemsBefore.includes(PermissionType.portalAdmin) && el !== PermissionType.portalAdmin)
          });
        }
        if (isCustomerUser && isPortalUser) {
          user = value.filter(el => {
            return (this.permissionItemsBefore.includes(PermissionType.customerUser) && el !== PermissionType.customerUser)
              || (this.permissionItemsBefore.includes(PermissionType.portalUser) && el !== PermissionType.portalUser)
          });
        }
        if (admin || user) {
          value = [...(admin || []), ...(user || [])];
          this.permissionControl.setValue(value)
        }
      }
      this.permissionItemsBefore = value;
    })
  }

  open(title: string) {
    this.subscribeOnControlChanges();
    this.title = title
    this.modalReference = this.modalService.open(this.templateRef, this.ngbModalOptions);
  }

  get firstNameControl() {
    return this.form.controls['firstName'];
  }

  get lastNameControl() {
    return this.form.controls['lastName'];
  }

  get emailControl() {
    return this.form.controls['email'];
  }

  get phoneControl() {
    return this.form.controls['phone'];
  }

  get customerControl() {
    return this.form.controls['customer'];
  }

  get orgControl() {
    return this.form.controls['organization'];
  }

  get permissionControl() {
    return this.form.controls['permission'];
  }

  get notesControl() {
    return this.form.controls['notes'];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && this.userData) {
      this.firstNameControl.setValue(this.userData.firstName)
      this.lastNameControl.setValue(this.userData.lastName);
      this.emailControl.setValue(this.userData.email)
      this.phoneControl.setValue(this.userData.phone);
      this.notesControl.setValue(this.userData.notes);
      this.customerControl.setValue(this.userData.customerId);
      this.orgControl.setValue(this.userData.organizationId);
      this.permissionControl.setValue(this.userData.permissions.map(el => el.name));
      this.emailControl.disable();
    }
  }

  getDataFromForm(): CreateUpdateUserDto {
    const user = new CreateUpdateUserDto();
    user.firstName = this.firstNameControl.value;
    user.lastName = this.lastNameControl.value;
    user.phone = this.phoneControl.value?.e164Number;
    user.email = this.emailControl.value;
    user.organizationId = this.orgControl.value;
    user.permissions = this.permissionControl.value;
    user.notes = this.notesControl.value;
    return user;
  }

  save() {
    this.trimControlsValue();
    this.form.markAllAsTouched();
    if (this.form.invalid) { return; }
    this.showRequestLoader = true;
    this.clearErrorMsg();
    const userInfo = this.getDataFromForm();
    if (this.userData) {
      this.updateUserRequest(userInfo);
    } else {
      this.createUserRequest(userInfo);
    }
  }

  clearErrorMsg() {
    this.existUserErr = false;
    this.incorrectOrgErr = false;
    this.incorrectPermissionErr = false;
    this.showOtherError = false;
  }

  trimControlsValue() {
    if (this.firstNameControl.value) { this.firstNameControl.setValue(this.firstNameControl.value.trim()); }
    if (this.lastNameControl.value) { this.lastNameControl.setValue(this.lastNameControl.value.trim()); }
  }

  createUserRequest(userInfo: CreateUpdateUserDto) {
    this.http.createUser(userInfo).subscribe(() => {
      this.successUserCreateEmitMsg();
      this.closeModal();
    }, (err) => {
      this.existErrors(err);
    }).add(() => {
      this.showRequestLoader = false;
    });
  }

  updateUserRequest(userInfo: CreateUpdateUserDto) {
    this.http.updateUser(this.userData.id, userInfo).subscribe(() => {

      this.permissionUpdate(userInfo);
    }, (err) => {
      this.existErrors(err);
    }).add(() => {
      this.showRequestLoader = false;
    });
  }

  permissionUpdate(userInfo: CreateUpdateUserDto) {
    const permissionsBeforeChanges = this.userData.permissions.map(el => el.name) as string[];
    const addPermissions = userInfo.permissions.filter(el => !permissionsBeforeChanges.includes(el));
    const removePermissions = this.userData.permissions.filter(el => !userInfo.permissions.includes(el.name));

    if (addPermissions.length > 0) { // adding new permissions
      const permissions = new CreatePermissionDto();
      permissions.permissions = addPermissions;
      this.http.createPermissionById(this.userData.id, permissions).subscribe(() => {
        this.removePermissions(removePermissions);
        this.successUserUpdateEmitMsg();
        this.closeModal();
      }, (err) => {
        this.existErrors(err);
      });
    } else {
      this.removePermissions(removePermissions);
      this.successUserUpdateEmitMsg();
      this.closeModal();
    }
  }

  removePermissions(permissions: PermissionDto[]) {
    permissions.forEach(el => { // remove permissions
      this.http.deletePermissionById(this.userData.id, el.id).subscribe();
    });
  }

  successUserCreateEmitMsg() {
    this.successUpdateTable.emit(true); // true for success createUser info modal showing
  }

  successUserUpdateEmitMsg() {
    this.successUpdateTable.emit(false); // false for success updateUser info modal showing
  }

  existErrors(err: HttpErrorResponse) {
    if (err.status === 400) {
      let message = err.error.message;
      if (!message && err.error.errors) {
        message = JSON.stringify(err.error.errors);
      }
      if (message) {
        this.incorrectOrgErr = message.toLocaleLowerCase().includes('incorrect organization');
        this.incorrectPermissionErr = message.toLocaleLowerCase().includes('incorrect permission');
        this.existUserErr = message.toLocaleLowerCase().includes('is already taken');
        if (!this.incorrectOrgErr && !this.existUserErr && !this.incorrectPermissionErr) {
          if (!message) {
            this.showErrorModal.emit();
          } else {
            this.otherError = message;
            this.showOtherError = true;
          }
        }
      } else {
        this.showErrorModal.emit();
      }
    }
    if (err.status === 500) {
      this.showErrorModal.emit();
    }
  }

  closeModal() {
    this.clearErrorMsg()
    this.emailControl.enable();
    this.form.reset();
    this.modalReference.close();
  }

  enableSaveBtn() {
    return this.userData?.firstName !== this.firstNameControl.value ||
      this.userData?.lastName !== this.lastNameControl.value ||
      this.userData?.notes !== this.notesControl.value ||
      this.userData?.phone !== this.phoneControl.value?.e164Number ||
      this.userData?.organizationId !== this.orgControl.value ||
      !this.helper.isMatchedToArrays(this.userData?.permissions.map(el => el.name), this.permissionControl.value);
  }

}
